import cloneDeep from 'lodash.clonedeep'
import LogRocket from 'logrocket'

import { LOGROCKET_TOKEN } from '../config/config'
import { getGitTag } from '../version'
import { sha256 } from './hasher'
import { redactObject } from './redactObject'

const REQUEST_WHITELISTED_FIELDS = [
  'accept',
  'content-length',
  'content-type',
  'contenttype',
  'credentials',
  'duration',
  'entryType',
  'initiatorType',
  'method',
  'mode',
  'name',
  'referrer',
  'reqId',
  'startTime',
  'transferSize',
  'url',
  // Middleware related fields
  '__typename',
  'Active__c',
  'Amount__c',
  'apollographql-client-name',
  'apollographql-client-version',
  'channel',
  'Description__c',
  'entityid',
  'HSACard__c',
  'Label',
  'operationName',
  'Payment_Profile__c',
  'paymentType',
  'PaymentType__c',
  'query',
  'Schedule_Date__c',
  'Source_URL__c',
  'Type__c',
  'UTM_Campaign__c',
  'UTM_Medium__c',
  'UTM_Source__c',
  'UTM_Term__c',
  'Web_MD_Domain__c',
].map((key) => key.toLowerCase())

const RESPONSE_WHITELISTED_FIELDS = [
  'cf-cache-status',
  'cf-ray',
  'content-type',
  'method',
  'message',
  'reqId',
  'server',
  'status',
  'x-transaction-id',

  // /authenticate endpoint related fields
  'balance',
  'estimatedPaidThrough',
  'id',
  // Middleware related fields
  '__typename',
  'Active__c',
  'Amount__c',
  'Description__c',
  'HSACard__c',
  'Id',
  'Label',
  'Name',
  'Payment_Profile__c',
  'paymentType',
  'PaymentType__c',
  'Schedule_Date__c',
  'Source_URL__c',
  'Type__c',
  'UTM_Campaign__c',
  'UTM_Medium__c',
  'UTM_Source__c',
  'UTM_Term__c',
  'Web_MD_Domain__c',
].map((key) => key.toLowerCase())

// Keys allowed to stay readable in a LogRocket log payload: the `extra`/`tags` of a
// captureMessage/captureException and the properties of a track event. Anything else is
// hashed by redactObject, so a call site can't put member PII (name, email, phone, date
// of birth, physician) into a log by accident. Mirrors the network allowlists above:
// opt-in rather than opt-out, so a newly logged field is redacted until listed here.
const LOG_WHITELISTED_FIELDS = [
  'checkedOptIn',
  'error',
  'invoiceId',
  'operation',
  'other',
  'pageName',
  'tokenExpirationDate',
  'tokenIssuedDate',
].map((key) => key.toLowerCase())

// The logrocket package exports its option types inside a non-exported namespace, so
// derive them from the method signatures instead. They stay correct across upgrades.
type CaptureOptions = NonNullable<Parameters<typeof LogRocket.captureMessage>[1]>
type TrackEventProperties = NonNullable<Parameters<typeof LogRocket.track>[1]>

/**
 * Hash every non-whitelisted value in a log payload
 *
 * @param payload `extra`/`tags` object, or track event properties
 * @returns a redacted copy, or undefined when there is no payload
 */
const redactLogPayload = <T extends object>(payload: T | undefined): T | undefined =>
  payload && redactObject(payload, LOG_WHITELISTED_FIELDS)

/**
 * Redact the metadata attached to a captured message or exception
 *
 * @param options capture options as passed by the call site
 * @returns the same options with `tags` and `extra` redacted
 */
const redactCaptureOptions = (options: CaptureOptions | undefined): CaptureOptions | undefined =>
  options && {
    ...options,
    tags: redactLogPayload(options.tags),
    extra: redactLogPayload(options.extra),
  }

/**
 * LogRocket.captureMessage with the metadata redacted first.
 * Always use this instead of importing LogRocket directly.
 *
 * @param message identifier; never interpolate member data into it, it is not redacted
 * @param options error metadata; only LOG_WHITELISTED_FIELDS stay readable
 */
export const captureMessage = (message: string, options?: CaptureOptions) => {
  LogRocket.captureMessage(message, redactCaptureOptions(options))
}

/**
 * LogRocket.captureException with the metadata redacted first.
 * Always use this instead of importing LogRocket directly.
 *
 * @param exception error instance; its message is not redacted
 * @param options error metadata; only LOG_WHITELISTED_FIELDS stay readable
 */
export const captureException = (exception: Error, options?: CaptureOptions) => {
  LogRocket.captureException(exception, redactCaptureOptions(options))
}

/**
 * LogRocket.track with the event properties redacted first.
 * Always use this instead of importing LogRocket directly.
 *
 * @param eventName name of the tracked event
 * @param eventProperties event metadata; only LOG_WHITELISTED_FIELDS stay readable
 */
export const track = (eventName: string, eventProperties?: TrackEventProperties) => {
  LogRocket.track(eventName, redactLogPayload(eventProperties))
}

/**
 * Initialize LogRocket, run network request/response sanitization, & identify
 *
 * @param prefix unknown field string
 * @param identifier unknown field string
 */
export const initLogRocket = async (prefix: string, identifier: string) => {
  // Skip LogRocket initialization if no token is configured
  if (!LOGROCKET_TOKEN) {
    return
  }

  const gitTag = getGitTag()

  LogRocket.init(LOGROCKET_TOKEN, {
    shouldCaptureIP: false,
    release: gitTag || undefined,
    network: {
      requestSanitizer: (request) => {
        const obj = cloneDeep(request)
        if (obj.body) {
          try {
            const body = JSON.parse(obj.body)
            obj.body = body
            // eslint-disable-next-line no-empty
          } catch {}
        }
        return redactObject(obj, REQUEST_WHITELISTED_FIELDS, false)
      },
      responseSanitizer: (response) => {
        const obj = cloneDeep(response)
        if (obj.body) {
          try {
            const body = JSON.parse(obj.body)
            obj.body = body
            // eslint-disable-next-line no-empty
          } catch {}
        }
        return redactObject(obj, RESPONSE_WHITELISTED_FIELDS, false)
      },
    },
  })
  const hashed = await sha256(identifier)
  LogRocket.identify(`${prefix}-${hashed}`, {
    gitTag: gitTag || 'dev',
  })
}
